import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Receipt, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PricingItem } from '@/types'
import { formatBaht as formatBahtLib } from '@/lib/money'
import { usePricingCalculation } from '@/hooks/usePricingCalculation'
import { FormulaTransparency } from '@/components/properties/FormulaTransparency'
import { Card } from '@/components/ui/Card'
import { Button } from './Button'

export type PricingFormula = PricingItem[]

export interface PricingPreviewProps {
  formula: PricingFormula
  sampleConsumption?: Record<string, number>
  className?: string
  // EP-58: condominium previews use unit.area_sqm × per_sqm rate and
  // unit.share_ratio × per_share_ratio rate. Apartments leave both undefined.
  unitAreaSqm?: number
  unitShareRatio?: number
}

const DEFAULT_CONSUMPTION: Record<string, number> = {
  water: 10,
  electricity: 50,
}

// EP-58: sample unit context for the property-level preview (Step 2 wizard)
// when the formula has per_sqm or per_share_ratio entries but no real unit
// exists yet. Lets the preview render a meaningful number instead of skipping
// the row entirely.
const SAMPLE_UNIT_AREA_SQM = 50
const SAMPLE_UNIT_SHARE_RATIO = 0.005

/**
 * Format a number as Thai baht: ฿X,XXX (no decimal for whole numbers).
 * Delegates to the shared formatBaht primitive from @/lib/money.
 */
function formatBaht(amount: number): string {
  return formatBahtLib(amount)
}

interface LineItem {
  key: string
  label: string
  amount: number
  detail?: string
}

/**
 * EP-56 L1c — `computeBreakdown` was deleted. The Step 2 preview now goes
 * through POST /api/pricing_calculation (backed by Ruby PricingEngine), so the
 * preview and the Step 7 invoice share one computation path and cannot
 * diverge again.
 *
 * Local mapping (engine line → display LineItem) for percentage `detail`:
 * the engine doesn't produce the "3% × ฿X,XXX" detail string itself, so we
 * derive it here from the engine's subtotal_before_percentage. All other
 * `detail` strings come from the engine's `description` field.
 */
function deriveDetailForLine(
  line: { item_type: string; description: string; quantity: number; unit_price: number; amount: number },
  formula: PricingFormula,
  subtotalBeforePercentage: number,
): string | undefined {
  // EP-58 R1: VAT sibling lines have item_type "<key>_vat" — they share the
  // engine's "VAT (7%) - Building maintenance" description, so just expose it.
  if (line.item_type.endsWith('_vat')) {
    return line.description.split('-').pop()?.trim()
  }

  const formulaItem = formula.find((f) => f.key === line.item_type)
  if (!formulaItem) return undefined

  if (formulaItem.type === 'percentage') {
    const pct = formulaItem.rate ?? 0
    return `${pct}% × ${formatBaht(subtotalBeforePercentage)}`
  }
  if (formulaItem.type === 'metered') {
    const rate = formulaItem.rate ?? 0
    const usage = line.quantity
    const minimum = formulaItem.minimum ?? 0
    return usage > 0
      ? `${usage} × ${formatBaht(rate)}`
      : minimum > 0
        ? `min ${formatBaht(minimum)}`
        : undefined
  }
  if (formulaItem.type === 'per_sqm') {
    const rate = formulaItem.rate ?? 0
    return `${line.quantity} m² × ${formatBaht(rate)}`
  }
  if (formulaItem.type === 'per_share_ratio') {
    const rate = formulaItem.rate ?? 0
    // quantity is always 1; the actual share comes from unit context. Engine
    // bakes the share into amount, so show "share × ฿X" with the formatted base.
    return `share × ${formatBaht(rate)} base`
  }
  return undefined
}

function getUnitPrice(formula: PricingFormula): number {
  const rent = formula.find((i) => i.type === 'fixed' && i.key === 'rent')
  return rent?.amount ?? 0
}

function getDisplayLabel(line: { item_type: string }, formula: PricingFormula): string {
  // EP-58 R1 — VAT sibling lines reference the parent key (e.g. "internet_vat"
  // → look up the "internet" formula entry).
  const isVat = line.item_type.endsWith('_vat')
  const lookupKey = isVat ? line.item_type.replace(/_vat$/, '') : line.item_type
  const f = formula.find((i) => i.key === lookupKey)
  const base = f?.name ?? (line.item_type === 'rent' ? 'Rent' : line.item_type)
  return isVat ? `VAT — ${base}` : base
}

/**
 * PricingPreview — live "sample bill" panel.
 *
 * Desktop (md+): renders as a static card in normal document flow.
 * Mobile (<md): renders as a sticky bottom bar. Collapsed shows only the total;
 *   tap [Show] to expand the full breakdown.
 */
export function PricingPreview({
  formula,
  sampleConsumption,
  className = '',
  unitAreaSqm,
  unitShareRatio,
}: PricingPreviewProps) {
  const { t } = useTranslation('properties')
  const { t: tc } = useTranslation('common')
  const [mobileExpanded, setMobileExpanded] = useState(false)

  const consumption = sampleConsumption ?? DEFAULT_CONSUMPTION
  const unitPrice = getUnitPrice(formula)

  // EP-58 R5 — supply a sample unit context when the formula has per_sqm or
  // per_share_ratio rules but no unit is bound (e.g., Step 2 of the wizard
  // before any unit exists). Apartments without those rule types ignore both
  // values entirely.
  const hasPerSqm = formula.some((f) => f.type === 'per_sqm')
  const hasPerShare = formula.some((f) => f.type === 'per_share_ratio')
  const effectiveArea = unitAreaSqm ?? (hasPerSqm ? SAMPLE_UNIT_AREA_SQM : undefined)
  const effectiveShare = unitShareRatio ?? (hasPerShare ? SAMPLE_UNIT_SHARE_RATIO : undefined)

  // EP-56 L1c — fetch the canonical breakdown from POST /api/pricing_calculation.
  // The endpoint is backed by Ruby PricingEngine, same module the Step 7
  // invoice uses. preview = invoice guaranteed.
  const { data, isLoading, isError, refetch } = usePricingCalculation(
    formula, unitPrice, consumption, true, effectiveArea, effectiveShare,
  )

  const lines: LineItem[] =
    data?.items.map((item) => ({
      key: item.item_type,
      label: getDisplayLabel(item, formula),
      amount: item.amount,
      detail: deriveDetailForLine(item, formula, data.subtotal_before_percentage),
    })) ?? []
  const total = data?.total ?? 0

  // F10/AC-8 (EP-158): used to join the raw consumption keys ("water",
  // "electricity") straight into the subtitle, so a Thai screen read
  // "ตัวอย่างบิล (10 water + 50 electricity)". Localized labels, composed
  // through one key so each locale can order value/label/unit its own way.
  const consumptionLabels: Record<string, string> = {
    water: t('pricing_preview.water_label'),
    electricity: t('pricing_preview.electricity_label'),
  }
  const consumptionSummary = Object.entries(consumption)
    .map(([key, val]) =>
      t('pricing_preview.consumption_item', { value: val, label: consumptionLabels[key] ?? key }),
    )
    .join(' + ')

  const breakdownRows = isError ? (
    <div className="text-sm text-warning py-2">
      <p>{t('pricing_preview.error')}</p>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="mt-1"
        onClick={() => refetch()}
      >
        {tc('actions.retry')}
      </Button>
    </div>
  ) : isLoading || !data ? (
    <div className="space-y-2 py-1" aria-label={t('pricing_preview.loading')}>
      <div className="h-4 w-full bg-base-200 animate-pulse rounded" />
      <div className="h-4 w-full bg-base-200 animate-pulse rounded" />
      <div className="h-4 w-full bg-base-200 animate-pulse rounded" />
    </div>
  ) : (
    <table className="w-full text-sm" aria-label={t('pricing_preview.breakdown_aria_label')}>
      <tbody>
        {lines.map((line) => (
          <tr key={line.key}>
            <td className="py-1 text-base-content/80">
              {line.label}
              {line.detail && (
                <span className="text-base-content/50 ml-1 text-xs">({line.detail})</span>
              )}
            </td>
            <td className="py-1 text-right tabular-nums font-medium">{formatBaht(line.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  // EP-114 T013 S2-12/S2-13 — desktop-only restyle: row labels move from the
  // dimmed `text-base-content/80` to the `text-fd-grey-dark` token (#504c49).
  // The `<table>` markup + `breakdown_aria_label` are preserved verbatim
  // (S2-13 — cosmetic only, do not break the a11y attribute). Kept as a
  // SEPARATE element from `breakdownRows` above (not a shared-variable edit)
  // so the mobile bottom sheet — which reuses `breakdownRows` — stays
  // byte-identical; the mobile sheet's own restyle is a later task (T017).
  const desktopBreakdownRows = isError ? (
    <div className="text-sm text-warning py-2">
      <p>{t('pricing_preview.error')}</p>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="mt-1"
        onClick={() => refetch()}
      >
        {tc('actions.retry')}
      </Button>
    </div>
  ) : isLoading || !data ? (
    <div className="space-y-2 py-1" aria-label={t('pricing_preview.loading')}>
      <div className="h-4 w-full bg-base-200 animate-pulse rounded" />
      <div className="h-4 w-full bg-base-200 animate-pulse rounded" />
      <div className="h-4 w-full bg-base-200 animate-pulse rounded" />
    </div>
  ) : (
    <table className="w-full text-sm" aria-label={t('pricing_preview.breakdown_aria_label')}>
      <tbody>
        {lines.map((line) => (
          <tr key={line.key}>
            <td className="py-1 text-fd-grey-dark">
              {line.label}
              {line.detail && (
                <span className="text-base-content/50 ml-1 text-xs">({line.detail})</span>
              )}
            </td>
            <td className="py-1 text-right tabular-nums font-medium">{formatBaht(line.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  const totalRow = (
    <div className="flex justify-between items-center font-semibold text-base border-t border-base-300 pt-2 mt-2">
      <span>{tc('labels.total')}</span>
      <span className="tabular-nums">{formatBaht(total)}</span>
    </div>
  )

  // EP-114 T013 S2-12 — desktop-only restyle of the total row: "รวม" label
  // 15px bold, amount 20px bold in text-primary (#004C50). Kept as a SEPARATE
  // element from `totalRow` above (not a shared-variable edit) so the mobile
  // bottom sheet — which reuses `totalRow` — stays byte-identical; the mobile
  // sheet's own restyle is a later task (T017).
  const desktopTotalRow = (
    <div className="flex justify-between items-center border-t border-base-300 pt-2 mt-2">
      <span className="text-[15px] font-bold">{tc('labels.total')}</span>
      <span className="text-xl font-bold text-primary tabular-nums">{formatBaht(total)}</span>
    </div>
  )

  // B-059: this panel is an illustrative estimate at SAMPLE usage and excludes
  // common-area fees (a property-level charge added at invoice time). The real
  // invoice uses recorded readings + CAF, so the totals legitimately differ —
  // disclose it here so the sample doesn't read as a data error vs the bill.
  const estimateNote = !isLoading && !isError && data && (
    <p className="text-xs text-base-content/50 mt-2 leading-snug">
      {t('pricing_preview.estimate_note')}
    </p>
  )

  return (
    <>
      {/* Desktop: always visible, in-flow card (md+).
          EP-114 T013 S2-12 — body p-6 gap-4 rounded-xl (was p-4 space-y-3);
          header 20px bold; total row + breakdown row labels restyled via the
          desktop-only `desktopTotalRow`/`desktopBreakdownRows` above so the
          mobile bottom sheet (which reuses the original `totalRow`/
          `breakdownRows`) is untouched. */}
      <Card
        className={`hidden md:block border border-base-300 shadow-sm ${className}`}
        bodyClassName="p-6 gap-4 rounded-xl"
        aria-label={t('pricing_preview.aria_label')}
      >
          <div>
            <h3 className="font-bold text-xl">{t('pricing_preview.title')}</h3>
            {consumptionSummary && (
              <p className="text-xs text-base-content/50 mt-0.5">({consumptionSummary})</p>
            )}
          </div>
          <div className="border-t border-base-300 pt-3">
            {desktopBreakdownRows}
            {desktopTotalRow}
            {estimateNote}
          </div>
          {/* EP-56 L3 — show the math being applied for each percentage item */}
          <FormulaTransparency formula={formula} data={data} />
      </Card>

      {/* Mobile (<md): floating icon button → half-screen bottom sheet with the
          full breakdown.

          Bottom offset: 5.25rem (84px) above the safe-area inset. This started
          as 6.5rem (104px), the 1.4.3 on-device fix that lifted the button clear
          of the wizard's fixed footer. It was lowered by 20px on 2026-08-04
          (owner) after that footer got shorter: the height revert took the
          footer buttons from h-10 back to size="sm", so the old offset left a
          visible gap. Anything that changes the footer height must re-check
          this number — the button must still clear OnboardingBottomBar, which
          is `fixed bottom-0` and taller than it looks because of its
          pb-[max(env(safe-area-inset-bottom),0.75rem)].

          Size reduced 56px -> 48px (owner, 2026-08-05) so it sits comfortably
          inside the import flow's confirm band rather than overhanging it. The
          OFFSET is deliberately untouched: it is the guarded number above, tuned
          on-device against the wizard footer, and this button renders in four
          places (wizard step 2, property Pricing tab, unit rate override, import
          review) where no band exists. 48px still clears the 44px tap-target
          floor. Centring against the band is tuned on the BAND, which is
          import-only and new, not here. */}
      <button
        type="button"
        onClick={() => setMobileExpanded(true)}
        aria-label={t('pricing_preview.title')}
        aria-expanded={mobileExpanded}
        data-testid="pricing-preview-mobile-toggle"
        className="md:hidden fixed bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] right-4 z-40 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-content shadow-lg"
      >
        <Receipt className="w-5 h-5" aria-hidden="true" />
      </button>

      {mobileExpanded && createPortal(
        <>
          {/* PORTALLED TO document.body (2026-08-04) — this is load-bearing, not
              tidying. The sheet is z-[80] and the wizard footer is z-30, so on
              z-index alone the sheet wins. It did not: the step-2 layout wraps
              this subtree in `relative z-10 mx-auto w-full max-w-[1172px]`,
              which opens a stacking context. That scoped the sheet's z-[80]
              INSIDE it, so the real comparison became the wrapper's 10 against
              the footer's 30 — the footer won and painted over the sheet's own
              sticky bottom section (title + collapse + total), clipping it.
              Portalling to document.body puts the backdrop and sheet in the
              root stacking context, where their z-values mean what they say.
              Matches the existing Lightbox.tsx pattern.

              EP-114 T017 S2M-9 — the z-[70]/z-[80] values themselves predate
              this task and sit off the CLAUDE.md BottomSheet ladder (documented
              `BottomSheet` = z-[70]); left as-is per the plan
              (docs/ep-114-onboarding-ui-update.md §5.4 S2M-9) rather than risk
              a ladder-wide realignment here. */}
          <div
            className="md:hidden fixed inset-0 z-[70] bg-black/40"
            onClick={() => setMobileExpanded(false)}
            aria-hidden="true"
          />
          {/* EP-114 T017 S2M-3/S2M-5 — 24px top radius + the Figma drop-shadow
              (was `rounded-t-2xl` 16px / `shadow-xl`). S2M-6 — max-h-[60vh] is
              already within tolerance of the Figma's ~54vh, left unchanged. */}
          <div
            className="md:hidden fixed inset-x-0 bottom-0 z-[80] max-h-[60vh] flex flex-col bg-base-100 rounded-t-[24px] shadow-[0_-8px_32px_rgba(15,23,42,0.08)]"
            role="dialog"
            aria-modal="true"
            aria-label={t('pricing_preview.title')}
          >
            {/* EP-114 T017 S2M-4 — decorative drag handle only; the sheet has
                no real swipe-to-dismiss gesture, this purely matches the Figma. */}
            <div className="flex justify-center pt-2 pb-1" aria-hidden="true">
              <div className="w-10 h-[5px] bg-base-300 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
              <div>
                <h3 className="font-semibold text-base">{t('pricing_preview.title')}</h3>
                {consumptionSummary && (
                  <p className="text-xs text-base-content/50">({consumptionSummary})</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMobileExpanded(false)}
                aria-label={tc('actions.close')}
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 py-4">
                {breakdownRows}
                {totalRow}
                {estimateNote}
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
